import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { loadDraft, saveDraft, clearDraft } from "../../lib/onboarding-storage";
import OnboardingLayout from "./OnboardingLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Loader2, ShieldCheck, Lock, Sparkles, ArrowRight } from "lucide-react";

// Fallback list — replaced with the /api/catalog response on mount.
const FALLBACK_COUNTRIES = [
  "United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman",
  "Egypt", "Jordan", "Morocco", "Nigeria", "Kenya", "South Africa",
  "United Kingdom", "United States", "Canada", "Germany", "France", "Netherlands",
  "India", "Pakistan", "Singapore", "Malaysia", "Indonesia", "Philippines",
  "Australia", "Sri Lanka", "Global",
];

const TRUST_BADGES = [
  { icon: Sparkles, label: "14-day free trial" },
  { icon: ShieldCheck, label: "Cancel anytime" },
  { icon: Lock, label: "SOC-grade security" },
];

export default function Step1Account() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token") || "";
  const inviteCode = searchParams.get("code") || "";

  const draft = loadDraft("step1") || {};
  const [firstName, setFirstName] = useState(draft.firstName || "");
  const [lastName, setLastName] = useState(draft.lastName || "");
  const [email, setEmail] = useState(draft.email || "");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState(draft.country || "");
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  const [submitting, setSubmitting] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);

  useEffect(() => {
    api
      .get("/catalog")
      .then((r) => {
        if (r.data?.countries?.length) setCountries(r.data.countries);
      })
      .catch(() => {
        /* keep fallback */
      });
    if (inviteToken || inviteCode) {
      api
        .get("/invites/preview", { params: { token: inviteToken, code: inviteCode } })
        .then((r) => setInvitePreview(r.data))
        .catch(() => setInvitePreview({ valid: false, error: "Invite lookup failed" }));
    }
  }, [inviteToken, inviteCode]);

  useEffect(() => {
    saveDraft("step1", { firstName, lastName, email, country });
  }, [firstName, lastName, email, country]);

  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    password.length >= 8 &&
    country;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await register({
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        email: email.trim().toLowerCase(),
        password,
        company: "",
        primary_country: country,
        countries: [country],
        primary_language: "en",
        time_zone: "UTC",
        currency: "USD",
        invite_code: inviteCode || null,
        invite_token: inviteToken || null,
      });
      clearDraft("step1");
      nav("/onboarding/practice");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingLayout
      currentStep="account"
      eyebrow="Step 1 · Account"
      title="Create your account"
      subtitle="Takes about 90 seconds. You'll launch your first Growth Campaign right after."
      estimatedMinutes="~90 sec"
    >
      {invitePreview?.valid && (
        <div
          className="mb-6 rounded-lg border border-accent/40 bg-accent/10 px-4 py-3 text-sm"
          data-testid="invite-preview"
        >
          <span className="text-accent font-semibold">Invite accepted</span>
          <span className="text-muted-foreground">
            {" "}
            · {invitePreview.plan} plan
            {invitePreview.note ? ` — ${invitePreview.note}` : ""}
          </span>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5" data-testid="onboarding-step1-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName" className="text-sm">
              First name
            </Label>
            <Input
              id="firstName"
              data-testid="onb-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-2"
              placeholder="Amir"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="lastName" className="text-sm">
              Last name
            </Label>
            <Input
              id="lastName"
              data-testid="onb-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="mt-2"
              placeholder="Hassan"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email" className="text-sm">
            Work email
          </Label>
          <Input
            id="email"
            data-testid="onb-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2"
            placeholder="amir@advisory.com"
          />
        </div>

        <div>
          <Label htmlFor="password" className="text-sm">
            Password
          </Label>
          <Input
            id="password"
            data-testid="onb-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2"
            placeholder="At least 8 characters"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            You can change this later from your profile.
          </p>
        </div>

        <div>
          <Label className="text-sm">Home market</Label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="mt-2" data-testid="onb-country">
              <SelectValue placeholder="Where do you primarily operate?" />
            </SelectTrigger>
            <SelectContent>
              {countries.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            You can add more markets on the next screen.
          </p>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Link
            to="/login"
            className="text-sm text-muted-foreground hover:text-foreground transition"
            data-testid="onb-signin-link"
          >
            Already have an account? Sign in
          </Link>
          <Button
            type="submit"
            size="lg"
            disabled={!canSubmit || submitting}
            data-testid="onb-step1-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <p className="text-xs text-muted-foreground/80 pt-1">
          Next: tell us about your practice (~2 min)
        </p>
      </form>

      <div className="mt-10 pt-6 border-t border-border/60">
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-3"
          data-testid="onb-trust-row"
        >
          {TRUST_BADGES.map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-2 text-xs text-muted-foreground font-sans-ui"
            >
              <Icon className="w-3.5 h-3.5 text-accent/80" />
              {label}
            </span>
          ))}
        </div>
      </div>
    </OnboardingLayout>
  );
}
